/* eslint max-depth: ["error", 10] */
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import {
    type GetCellNameParams,
    getCellName,
} from './appletUtil';

type AppletEventDefSource = {
    kind: 'user';
} | {
    kind: 'btn' | 'input';
    cellId: string;
} | {
    kind: 'workInstruction';
};

export const workInstructionStepCompleteManualConditionEventId = 'complete-work-instruction-step-manual-condition';
export const workInstructionStepSkipEventId = 'skip-work-instruction-step';
export const workInstructionStepFailEventId = 'fail-work-instruction-step';
export const workInstructionStepCompleteEventId = 'complete-work-instruction-step';

/** centralized place to define how implied applet event ids are generated */
export const impliedAppletEventIds = {
    btnClick: (opts: { cellId: string }) => `${opts.cellId}:click`,
    inputSubmit: (opts: { cellId: string }) => `${opts.cellId}:submit`,
};

/** centralized place to define how implied applet event names are generated */
export const impliedAppletEventNames = {
    inputSubmit: (payload: GetCellNameParams<Latest.Screen.Cells.InputCell>) => {
        const inputCellName = getCellName(payload);
        return `${inputCellName} submitted`;
    },
    btnClick: (payload: GetCellNameParams<Latest.Screen.Cells.Btn.Cell>) => {
        const btnCellName = getCellName(payload);
        return `${btnCellName} clicked`;
    },
};

export type GetAppletEventsResult = Record<string, {
    def: Latest.AppletEvent.Def;
    source: AppletEventDefSource;
    // references: unknown[]; // FUTURE?
}>;
export function getImpliedAppletEvents (appletDesignVersionCfg: Latest.AppletDesignVersionConfig): GetAppletEventsResult {
    const result: GetAppletEventsResult = {};

    // implied events
    for (const screen of appletDesignVersionCfg.screens) {
        for (const cell of screen.cells) {
            if (cell.type === 'btn') {
                const noExplicitEvent = !cell.attrs.appletEventId;
                if (noExplicitEvent) {
                    const appletEventId = impliedAppletEventIds.btnClick({ cellId: cell.id });
                    const appletEventName = impliedAppletEventNames.btnClick({
                        cell,
                        defaultText: 'Unnamed Button',
                        distinguishableIdentifier: 'label',
                        designConfig: appletDesignVersionCfg,
                    });
                    result[appletEventId] = {
                        def: { appletEventId, appletEventName },
                        source: { kind: 'btn', cellId: cell.id },
                    };
                }
            } else if (cell.type === 'input') {
                const noExplicitEvent = !cell.attrs.appletEventId;
                if (noExplicitEvent) {
                    const appletEventId = impliedAppletEventIds.inputSubmit({ cellId: cell.id });
                    const appletEventName = impliedAppletEventNames.inputSubmit({
                        cell,
                        defaultText: 'Unnamed Input',
                        distinguishableIdentifier: 'label',
                        designConfig: appletDesignVersionCfg,
                    });
                    result[appletEventId] = {
                        def: { appletEventId, appletEventName },
                        source: { kind: 'input', cellId: cell.id },
                    };
                }
            }
        }
    }

    if (Object.values(appletDesignVersionCfg.flows).find(f => Object.values(f.nodes).find(n => n.kind === 'workInstruction'))) {
        result[workInstructionStepCompleteManualConditionEventId] = {
            def: {
                appletEventName: 'Complete Current Work Instruction Step Condition',
                appletEventId: workInstructionStepCompleteManualConditionEventId,
                description: 'Emit this event when the current work instruction step has a manual completion condition which should be completed',
            },
            source: {
                kind: 'workInstruction',
            },
        };
        result[workInstructionStepSkipEventId] = {
            def: {
                appletEventName: 'Skip Current Work Instruction Step',
                appletEventId: workInstructionStepSkipEventId,
                description: 'Emit this event to skip the current work instruction step',
            },
            source: {
                kind: 'workInstruction',
            },
        };
        result[workInstructionStepFailEventId] = {
            def: {
                appletEventName: 'Fail Current Work Instruction Step',
                appletEventId: workInstructionStepFailEventId,
                description: 'Emit this event to permanently fail the current work instruction step',
            },
            source: {
                kind: 'workInstruction',
            },
        };
        result[workInstructionStepCompleteEventId] = {
            def: {
                appletEventName: 'Force Complete Current Work Instruction Step',
                appletEventId: workInstructionStepCompleteEventId,
                description: 'Emit this event to mark the current work instruction step as complete, regardless of the step\'s conditions',
            },
            source: {
                kind: 'workInstruction',
            },
        };
    }

    return result;
}
/**
 * @param appletDesignCfg The entire applet design config
 *
 * By making this function the source of truth of allowed applet events,
 * we ensure that all possible locations for applet events are touched here.
 * No need for zod schema hooks which are unreliably used.
 */
export function getAppletEvents (appletDesignVersionCfg: Latest.AppletDesignVersionConfig): GetAppletEventsResult {
    const result: GetAppletEventsResult = {};

    // user defined applet events
    for (const appletEventDef of Object.values(appletDesignVersionCfg.userAppletEvents)) {
        result[appletEventDef.appletEventId] = {
            def: appletEventDef,
            source: { kind: 'user' },
        };
    }
    const impliedEvents = getImpliedAppletEvents(appletDesignVersionCfg);
    for (const [ appletEventId, appletEvent ] of Object.entries(impliedEvents)) {
        result[appletEventId] = appletEvent;
    }


    return result;
}
